/**
 * OpenSolar Token Manager
 *
 * This module handles the management of OpenSolar API and SDK tokens.
 * It provides functions to fetch, refresh and store the tokens for use with the OpenSolar SDK.
 */
import { OpenSolarSdkTokens, OpenSolarUser } from '@shared/opensolar-types';
import { apiRequest } from './queryClient';

// Cache the tokens in memory to avoid unnecessary API calls
let cachedTokens: OpenSolarSdkTokens | null = null;
let tokenExpiryTime: number | null = null;
let currentUser: OpenSolarUser | null = null;

// Token validity period (in milliseconds) - 10 minutes for safety
// The actual token lasts much longer, but we refresh well before expiry
const TOKEN_VALIDITY_MS = 10 * 60 * 1000;

/**
 * Get SDK tokens, either from cache or from the server
 * @returns A promise that resolves to the SDK tokens
 */
export async function getSdkTokens(): Promise<OpenSolarSdkTokens> {
  // Return cached tokens if they're still valid
  if (cachedTokens && tokenExpiryTime && Date.now() < tokenExpiryTime) {
    return cachedTokens;
  }

  // Otherwise fetch new tokens from the server
  try {
    const tokenResponse = await apiRequest('GET', '/api/opensolar/sdk-token');

    if (!tokenResponse.ok) {
      const errorData = await tokenResponse.json();
      throw new Error(errorData.message || 'Failed to get SDK tokens');
    }

    const tokens = await tokenResponse.json();
    cachedTokens = tokens;
    tokenExpiryTime = Date.now() + TOKEN_VALIDITY_MS;

    return tokens;
  } catch (error) {
    console.error('Error fetching OpenSolar SDK tokens:', error);
    throw error;
  }
}

/**
 * Force refresh of SDK tokens by invalidating the cache and fetching new tokens
 * @returns A promise that resolves to the new SDK tokens
 */
export async function refreshSdkTokens(): Promise<OpenSolarSdkTokens> {
  // Invalidate cache
  cachedTokens = null;
  tokenExpiryTime = null;

  // Fetch new tokens
  return getSdkTokens();
}

/**
 * Clear cached tokens (e.g., on logout)
 */
export function clearSdkTokens(): void {
  cachedTokens = null;
  tokenExpiryTime = null;
}

/**
 * Get the current OpenSolar user
 * @returns The current user or null if not authenticated
 */
export function getCurrentUser(): OpenSolarUser | null {
  return currentUser;
}

/**
 * Set the current OpenSolar user
 * @param user The user to set as current
 */
export function setCurrentUser(user: OpenSolarUser | null): void {
  currentUser = user;
}

/**
 * Clear all token data including user information
 */
export function clearTokenData(): void {
  clearSdkTokens();
  currentUser = null;
}
